import { get } from 'lodash'
import { useEffect, useState } from 'react'
import { FormattedMessage, IntlShape, MessageDescriptor, useIntl } from 'react-intl'
import styled from 'styled-components'

import {
  ConditionType,
  FilterProps
} from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/Boolean/Conditions/interfaces'
import { Operator } from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/Boolean/Conditions/utils'

import messages from './messages'

const BoldText = styled.div`
  font-weight: bold;
`

const Rule = styled.div<{ prefix?: string }>`
  /* If it has a prefix (and/or) use that, otherwise use a comma since it is the beginning of a new rule */
  &:before {
    content: ' ${props => props.prefix} ';
    font-style: italic;
  }
`

const ConditionRule = styled.div``

const RuleText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;

  ${BoldText}, ${Rule}, ${ConditionRule} {
    display: inline;
  }

  ${ConditionRule} {
    /* Insert a comma between conditions */
    &:before {
      content: ', ';
    }

    /* Remove the command if it's the first condition */
    &:first-child {
      &:before {
        content: '';
      }
    }
  }
`

/**
 * Mapping for which language prefix to use for certain attributes
 * In order to produce a more natural sounding sentence
 */
const prefixAttributeMapping = (filter: FilterProps) => {
  const leftComparator: FilterProps['leftComparator'] = get(filter, 'leftComparator')
  const rightComparator: FilterProps['rightComparator'] = get(filter, 'rightComparator', '')
  let message: MessageDescriptor
  switch (filter.operator) {
    case Operator.EQ: {
      message = messages.attributePossessive
      break
    }
    case Operator.NE: {
      message = messages.notAttributePossessive
      break
    }
    case Operator.CO: {
      message = messages.contains
      break
    }
    case Operator.SW: {
      message = messages.startsWith
      break
    }
    case Operator.EW: {
      message = messages.endsWith
      break
    }
    case Operator.PR: {
      message = messages.hasValue
      break
    }
    case Operator.GT: {
      message = messages.greaterThan
      break
    }
    case Operator.GE: {
      message = messages.greaterThanOrEqual
      break
    }
    case Operator.LT: {
      message = messages.lessThan
      break
    }
    case Operator.LE: {
      message = messages.lessThanOrEqual
      break
    }
    default: {
      message = messages.attributePossessive
    }
  }

  return (
    <FormattedMessage
      {...message}
      values={{
        leftComparator: <BoldText key={`${filter.id}.leftComparator`}>{leftComparator}</BoldText>,
        rightComparator: <BoldText key={`${filter.id}.rightComparator`}>{rightComparator}</BoldText>
      }}
    />
  )
}

interface DefineRuleProps {
  filters: FilterProps[]
}

const defineRule = ({ filters, intl }: DefineRuleProps & { intl: IntlShape }) => {
  return filters.reduce((prev, filter: FilterProps, idx) => {
    if ((filter.rightComparator !== undefined && filter.rightComparator !== '') || filter.operator === Operator.PR) {
      if (idx === 0) {
        return [...prev, <Rule key={`${filter.id}-${idx}`}>{prefixAttributeMapping(filter)}</Rule>]
      } else {
        return [
          ...prev,
          <Rule key={`${filter.id}-${idx}`} prefix={intl.formatMessage(messages.and)}>
            {prefixAttributeMapping(filter)}
          </Rule>
        ]
      }
    }
    return prev
  }, [] as JSX.Element[])
}

interface Props {
  conditions: ConditionType[]
}
/**
 * BooleanSimpleRule - Translates a conditions array into a human readable sentence
 *
 */
const BooleanSimpleRule = ({ conditions }: Props) => {
  const intl = useIntl()

  const [conditionText, setConditionText] = useState<{ id: string; rules: JSX.Element[] }[] | undefined>([])

  useEffect(() => {
    if (conditions && conditions.length > 0) {
      const filterText = conditions.reduce(
        (prev, condition: ConditionType) => {
          const rules = defineRule({ filters: condition.filters, intl })
          return [...prev, { id: condition.id, rules: rules }]
        },
        [] as { id: string; rules: JSX.Element[] }[]
      )

      if (filterText.some(filter => filter.rules.length === 0)) {
        setConditionText(undefined)
      } else {
        setConditionText(filterText)
      }
    } else {
      setConditionText(undefined)
    }
  }, [conditions, intl])

  const ruleText = conditionText

  return ruleText ? (
    <RuleText>
      {ruleText.length > 0 && (
        <FormattedMessage
          {...messages.conditionSentence}
          values={{
            conditionText: ruleText.map((condition, index) => {
              return <ConditionRule key={`condition-${index}`}>{condition.rules}</ConditionRule>
            })
          }}
        />
      )}
    </RuleText>
  ) : null
}

export default BooleanSimpleRule
